'use client'

export type DesktopMediaPermission = 'microphone' | 'camera' | 'screen_recording'

export type DesktopPermissionState = {
  ok?: boolean
  platform?: string
  permission?: string
  permissionLabel?: string
  status?: string
  permissionStatus?: string
  granted?: boolean
  alreadyGranted?: boolean
  prompted?: boolean
  openedSettings?: boolean
  settingsUrl?: string | null
  needsRelaunch?: boolean
  autoRelaunchPending?: boolean
  message?: string
  error?: string
}

type DesktopPermissionOptions = {
  requestSource?: string
  source?: string
  prompt?: boolean
  openSettings?: boolean
  autoRelaunch?: boolean
}

export type AudioInputDeviceOption = {
  deviceId: string
  groupId: string
  label: string
  isDefault: boolean
}

export type BrowserMicrophonePermissionState =
  | PermissionState
  | 'unsupported'
  | 'unknown'

export const VOICE_INPUT_DEVICE_STORAGE_KEY = 'losi.chat.voice-input-device-id'

type DesktopPermissionApi = {
  getStatus?: (permission: string) => Promise<DesktopPermissionState> | DesktopPermissionState
  request?: (
    permission: string,
    options?: DesktopPermissionOptions
  ) => Promise<DesktopPermissionState> | DesktopPermissionState
  openSettings?: (permission: string) => Promise<DesktopPermissionState> | DesktopPermissionState
}

type DocumentWithCapturePolicy = Document & {
  permissionsPolicy?: {
    allowsFeature?: (feature: string) => boolean
  }
  featurePolicy?: {
    allowsFeature?: (feature: string) => boolean
  }
}

function getDesktopPermissionApi(): DesktopPermissionApi | null {
  if (typeof window === 'undefined') {
    return null
  }

  const nested = window.electronAPI?.desktop?.permissions
  if (nested?.getStatus || nested?.request || nested?.openSettings) {
    return nested
  }

  const legacy = window.electronAPI
  if (
    legacy?.getDesktopPermissionStatus ||
    legacy?.requestDesktopPermission ||
    legacy?.openDesktopPermissionSettings
  ) {
    return {
      getStatus: legacy.getDesktopPermissionStatus,
      request: legacy.requestDesktopPermission,
      openSettings: legacy.openDesktopPermissionSettings
    }
  }

  return null
}

function normalizePermissionStatus(value: unknown) {
  const status = String(value || '').trim().toLowerCase()
  return status || 'unknown'
}

function permissionStateGranted(state: DesktopPermissionState | null | undefined) {
  if (!state) {
    return false
  }

  return state.granted === true || normalizePermissionStatus(state.status || state.permissionStatus) === 'granted'
}

function createPermissionError(permission: DesktopMediaPermission, state: DesktopPermissionState) {
  const label =
    typeof state.permissionLabel === 'string' && state.permissionLabel.trim()
      ? state.permissionLabel.trim()
      : permission === 'screen_recording'
        ? 'Screen recording'
        : permission === 'camera'
          ? 'Camera'
          : 'Microphone'
  const message =
    state.message ||
    state.error ||
    `${label} permission is required before this desktop action can continue.`
  const error = new Error(message)
  ;(error as Error & { permissionState?: DesktopPermissionState; permission?: string }).permissionState = state
  ;(error as Error & { permissionState?: DesktopPermissionState; permission?: string }).permission = permission
  return error
}

export function hasNativeDesktopPermissionApi() {
  return Boolean(getDesktopPermissionApi())
}

export function isDesktopMediaHost() {
  return typeof window !== 'undefined' && Boolean(window.electronAPI)
}

export async function getNativeDesktopPermissionStatus(
  permission: DesktopMediaPermission
) {
  const api = getDesktopPermissionApi()
  if (!api?.getStatus) {
    return null
  }

  return await api.getStatus(permission)
}

export async function requestNativeDesktopPermission(
  permission: DesktopMediaPermission,
  options: DesktopPermissionOptions = {}
) {
  const api = getDesktopPermissionApi()
  if (!api?.request) {
    return null
  }

  return await api.request(permission, options)
}

export async function ensureNativeDesktopPermission(
  permission: DesktopMediaPermission,
  options: DesktopPermissionOptions = {}
) {
  const state = await requestNativeDesktopPermission(permission, {
    prompt: true,
    openSettings: true,
    ...options
  })

  if (!state) {
    return null
  }

  if (!permissionStateGranted(state)) {
    throw createPermissionError(permission, state)
  }

  return state
}

function constraintsUseAudio(constraints: MediaStreamConstraints) {
  return constraints.audio === true || (typeof constraints.audio === 'object' && constraints.audio !== null)
}

function constraintsUseVideo(constraints: MediaStreamConstraints) {
  return constraints.video === true || (typeof constraints.video === 'object' && constraints.video !== null)
}

function getCaptureErrorName(error: unknown) {
  if (!error || typeof error !== 'object') {
    return null
  }

  const name = (error as { name?: unknown }).name
  return typeof name === 'string' ? name : null
}

function createBrowserMicrophoneError(message: string) {
  const error = new Error(message)
  ;(error as Error & { permission?: string }).permission = 'microphone'
  return error
}

function isMicrophoneCaptureDeniedError(error: unknown) {
  const name = getCaptureErrorName(error)
  return name === 'NotAllowedError' || name === 'SecurityError'
}

function getAudioInputFallbackLabel(device: MediaDeviceInfo, index: number) {
  if (device.deviceId === 'default') {
    return 'Default microphone'
  }

  if (device.deviceId === 'communications') {
    return 'Communications microphone'
  }

  return `Microphone ${index + 1}`
}

export function isGetUserMediaSupported() {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  )
}

export function isMediaRecorderCaptureSupported() {
  return isGetUserMediaSupported() && typeof MediaRecorder !== 'undefined'
}

export function isBrowserMediaCaptureSecureContext() {
  return typeof window === 'undefined' || window.isSecureContext
}

export function isMicrophoneAllowedByDocumentPolicy() {
  if (typeof document === 'undefined') {
    return true
  }

  const captureDocument = document as DocumentWithCapturePolicy
  const policy = captureDocument.permissionsPolicy ?? captureDocument.featurePolicy

  if (typeof policy?.allowsFeature !== 'function') {
    return true
  }

  try {
    return policy.allowsFeature('microphone')
  } catch {
    return true
  }
}

export async function getBrowserMicrophonePermissionState(): Promise<BrowserMicrophonePermissionState> {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.permissions?.query !== 'function'
  ) {
    return 'unsupported'
  }

  try {
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName
    })
    return status.state
  } catch {
    return 'unknown'
  }
}

async function assertBrowserMicrophoneCanPrompt() {
  if (!isBrowserMediaCaptureSecureContext()) {
    throw createBrowserMicrophoneError(
      'Microphone access requires HTTPS or localhost in Chrome and Brave.'
    )
  }

  if (!isMicrophoneAllowedByDocumentPolicy()) {
    throw createBrowserMicrophoneError(
      'Microphone access is blocked by this page policy.'
    )
  }

  const permissionState = await getBrowserMicrophonePermissionState()

  if (permissionState === 'denied') {
    throw createBrowserMicrophoneError(
      'Microphone access is blocked for this site. In Chrome or Brave, open site settings from the address bar and set Microphone to Allow.'
    )
  }
}

export function createAudioInputConstraints(deviceId?: string | null): MediaTrackConstraints {
  const constraints: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  }

  if (deviceId) {
    constraints.deviceId = { exact: deviceId }
  }

  return constraints
}

export function readPreferredAudioInputDeviceId() {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const deviceId = window.localStorage.getItem(VOICE_INPUT_DEVICE_STORAGE_KEY)
    return deviceId?.trim() || null
  } catch {
    return null
  }
}

export function persistPreferredAudioInputDeviceId(deviceId: string | null) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    if (deviceId) {
      window.localStorage.setItem(VOICE_INPUT_DEVICE_STORAGE_KEY, deviceId)
    } else {
      window.localStorage.removeItem(VOICE_INPUT_DEVICE_STORAGE_KEY)
    }
  } catch {
    // localStorage can be unavailable in constrained embedded contexts.
  }
}

export async function listAudioInputDevices(): Promise<AudioInputDeviceOption[]> {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.mediaDevices?.enumerateDevices !== 'function'
  ) {
    return []
  }

  const devices = await navigator.mediaDevices.enumerateDevices()

  return devices
    .filter((device) => device.kind === 'audioinput' && device.deviceId)
    .map((device, index) => ({
      deviceId: device.deviceId,
      groupId: device.groupId || '',
      label: device.label?.trim() || getAudioInputFallbackLabel(device, index),
      isDefault: device.deviceId === 'default'
    }))
}

export function getAudioInputDeviceIdFromStream(stream: MediaStream) {
  const [track] = stream.getAudioTracks()
  const settings = track?.getSettings()

  return typeof settings?.deviceId === 'string' && settings.deviceId.trim()
    ? settings.deviceId.trim()
    : null
}

export function isAudioInputDeviceUnavailableError(error: unknown) {
  if (typeof DOMException === 'undefined' || !(error instanceof DOMException)) {
    return false
  }

  return (
    error.name === 'NotFoundError' ||
    error.name === 'OverconstrainedError' ||
    error.name === 'NotReadableError' ||
    error.name === 'AbortError'
  )
}

export function isGetDisplayMediaSupported() {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  )
}

/**
 * Screen share / teach-by-showing: request OS screen-recording permission via
 * Electron first, then use Chromium getDisplayMedia for the MediaRecorder stream.
 */
export async function getDisplayMediaWithNativePermissions(
  constraints: DisplayMediaStreamOptions = { video: true, audio: true },
  options: DesktopPermissionOptions = {}
) {
  if (!isGetDisplayMediaSupported()) {
    throw new Error('Screen capture is not supported in this browser.')
  }

  await ensureNativeDesktopPermission('screen_recording', {
    requestSource: options.requestSource || options.source || 'screen_capture',
    ...options
  })

  return await navigator.mediaDevices.getDisplayMedia(constraints)
}

export async function getUserMediaWithNativePermissions(
  constraints: MediaStreamConstraints,
  options: DesktopPermissionOptions = {}
) {
  if (!isGetUserMediaSupported()) {
    throw new Error('Media capture is not supported in this browser.')
  }

  if (constraintsUseAudio(constraints)) {
    await assertBrowserMicrophoneCanPrompt()
    await ensureNativeDesktopPermission('microphone', {
      requestSource: options.requestSource || options.source || 'media_capture',
      ...options
    })
  }

  if (constraintsUseVideo(constraints)) {
    await ensureNativeDesktopPermission('camera', {
      requestSource: options.requestSource || options.source || 'media_capture',
      ...options
    })
  }

  try {
    return await navigator.mediaDevices.getUserMedia(constraints)
  } catch (error) {
    if (constraintsUseAudio(constraints) && isMicrophoneCaptureDeniedError(error)) {
      throw createBrowserMicrophoneError(
        'Microphone access was denied. In Chrome or Brave, open site settings from the address bar and allow Microphone for Losi.'
      )
    }

    throw error
  }
}
